import { createAction } from '@reduxjs/toolkit';
import * as api from '@codametrix/client-api';
import * as appServiceLines from '../core/service-lines';
import * as commonActions from './action-types';
import * as commonEnums from '../enums/index';
import { parseUCL108 } from '../core/amplify/procedure-parser';
import { loadCase } from './cases.action';
import { navigationIntent } from './navigation-intent.action';

const { Launch } = commonActions;
const { LaunchParams } = commonEnums;

const caseSearchAvailable = createAction<CMxAPI.CaseRun[]>(Launch.CASE_SEARCH);
const caseLookupError = createAction<Error>(Launch.CASE_LOOKUP_ERROR);

export const loadCaseFromUCLs = async (resultSet: any) => {
  if (resultSet.tracking) {
    for (let i = 0; i < resultSet.tracking.length; i++) {
      const procedureInfo = resultSet.tracking[i];
      try {
        const caseDetail = await api.requestJSON<CMxAPI.CaseRun>(
          `${procedureInfo.serviceLine.path}/processinstance/${procedureInfo.identifier}/details/v1`
        );
        if (caseDetail) {
          appServiceLines.set(procedureInfo.serviceLine);
          return caseDetail;
        }
      } catch (e) {
        /** squelch */
      }
    }
  }

  if (resultSet.standard) {
    for (let i = 0; i < resultSet.standard.length; i++) {
      const procedureInfo = resultSet.standard[i];
      let caseDetail;

      try {
        // radiology
        if (procedureInfo.serviceLine.path === '') {
          caseDetail = await api.requestJSON<CMxAPI.CaseRun>(
            `${procedureInfo.serviceLine.path}/caseinstance/procedureid/${procedureInfo.identifier}/v1`
          );
        } else {
          caseDetail = await api.requestJSON<CMxAPI.CaseRun>(
            `${procedureInfo.serviceLine.path}/caseinstance/transactionid/${procedureInfo.identifier}/v1`
          );
        }
        if (caseDetail) {
          appServiceLines.set(procedureInfo.serviceLine);
          return caseDetail;
        }
      } catch (e) {
        /** squelch */
      }
    }
  }

  throw new Error(`unable to locate a case from parsed ucl108 ids.`);
};

const runConxtextSearch = async (
  searchParams: URLSearchParams,
  dispatch: any
) => {
  const searchResults = await backupCaseSearch(searchParams);
  const resultCount = searchResults?.length ?? 0;
  if (resultCount === 1) {
    const [caseRun] = searchResults;
    const contextResult = await loadCase(caseRun, dispatch);
    dispatch(navigationIntent({ intent: 'CASE_DETAIL', url: contextResult.url }));
  } else if (resultCount > 1) {
    dispatch(caseSearchAvailable(searchResults));
    dispatch(navigationIntent({ intent: 'CASE_CHOOSER' }));
  } else {
    dispatch(navigationIntent({ intent: 'CASE_NOT_FOUND' }));
  }
};

const backupCaseSearch = async (searchParams: URLSearchParams) => {
  const options = {
    method: 'POST',
    body: JSON.stringify({
      patientMrn: `${searchParams.get(LaunchParams.PATIENT_ID)}`,
      dateOfService: `${searchParams.get(LaunchParams.VISIT_DATE)}`,
      providerNumber: `${searchParams.get(LaunchParams.PROVIDER_ID)}`,
      visitNumber: `${searchParams.get(LaunchParams.VISIT_CSN)}`
    })
  };
  const searchResults = await api.requestJSON<CMxAPI.CaseRun[]>(
    `${appServiceLines.get().path}/caseinstance/listbycontext/v1`,
    options
  );
  return searchResults;
};

const fetchCases = async (
  externalApp: CMxAPI.LaunchParams,
  dispatch: any
) => {
  let caseJson;

  try {
    const launchParams = new URLSearchParams(externalApp);
    const hasUCL108 = launchParams.has(LaunchParams.TRANSACTION_IDS);

    if (hasUCL108 && launchParams.get(LaunchParams.TRANSACTION_IDS) !== '') {
      // Lookup flow:
      // 1. parse UCL108
      // 2. attempt UCL108 search
      // 3. if no case found, do a context search
      const ucl108List = launchParams.get(LaunchParams.TRANSACTION_IDS);
      try {
        // 1. parse UCL108
        const results = parseUCL108(ucl108List);
        // 2. attempt UCL108 search
        const caseRun = await loadCaseFromUCLs(results);

        if (caseRun) {
          const fetchResult = await loadCase(caseRun, dispatch);
          dispatch(navigationIntent({ intent: 'CASE_DETAIL', url: fetchResult.url }));
        }
      } catch (e) {
        // 3. if no case found, do a context search
        await runConxtextSearch(launchParams, dispatch);
      }
    } else if (launchParams.get(LaunchParams.PATIENT_ID)) {
      await runConxtextSearch(launchParams, dispatch);
    } else {
      throw new Error(`Not enough information to lookup a case`);
    }
  } catch (e) {
    dispatch(navigationIntent({ intent: 'CASE_NOT_FOUND' }));
    dispatch(caseLookupError(e));
  }
  return caseJson;
};

export { caseLookupError, caseSearchAvailable, fetchCases };
